import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../api/apiClient';
import {
  DashboardKpi,
  DeliveryListItem,
  DeliveryListResponse,
  PaginatedDeliveryResponse,
  DeliveryDetail,
  DeliveryStatus,
  CreateCertificationRequest,
  CheckerVerificationRequest,
  CreateCheckRequest,
  RouteUniqueness,
  RouteTypeItem,
  RouteTypeListResponse,
  LoadSequenceItem,
  LoadSequenceListResponse,
  LocationRoute,
  LocationRouteListResponse,
  ImageUploadResponse,
} from './types';
import {
  EquipmentConfigItem,
  EquipmentListResponse,
  PhotoCategoryItem,
  PhotoCategoryListResponse,
} from '../Admin/pages/PerfectTruckManagement/types';

const getClientTimeParams = () => ({
  clientTimestamp: getLocalTimestamp(),
  clientTimezone: getTimezoneOffsetString(),
});

// ── Refresh Status ────────────────────────────────────────────────────────────

export interface RefreshStatusResponse {
  lastRefreshedLabel: string;
}

export const fetchRefreshStatus = async (): Promise<RefreshStatusResponse> => {
  const api = createApiClient();
  const res = await api.get<RefreshStatusResponse>('/perfect-truck-kpi/refresh-status');
  return res.data;
};

// ── Dashboard KPIs ────────────────────────────────────────────────────────────

export const fetchDashboardKpis = async (locationName?: string, filter?: string, deliveryType?: string): Promise<DashboardKpi> => {
  const api = createApiClient();
  const res = await api.get<DashboardKpi>('/perfect-truck/deliveries/kpi', {
    params: { 
      ...(locationName ? { locationName } : {}), 
      ...(filter ? { filter } : {}),
      ...(deliveryType ? { deliveryType } : {}),
    },
  });
  return res.data;
};

// ── Deliveries ────────────────────────────────────────────────────────────────

export const fetchDeliveries = async (
  filter: 'all' | 'open' | 'done' = 'all',
  locationName?: string,
  page: number = 0,
  size: number = 100,
  sort?: string,
  deliveryDate?: string | string[],
  feedbackPresent?: boolean,
  feedbackCategory?: string[],
  deliveryType?: string,
): Promise<PaginatedDeliveryResponse> => {
  const api = createApiClient();
  const params: Record<string, any> = {
    filter,
    page,
    size,
  };
  
  // Add optional filters
  if (locationName) {
    params.locationName = locationName;
  }
  if (sort) {
    params.sort = sort;
  }
  if (deliveryDate) {
    params.deliveryDate = deliveryDate;
  }
  if (feedbackPresent === true) {
    params.feedbackPresent = true;
  }
  if (feedbackCategory && feedbackCategory.length > 0) {
    params.feedbackCategory = feedbackCategory;
  }

  const res = await api.get<PaginatedDeliveryResponse>('/perfect-truck/deliveries', {
    
    paramsSerializer: { indexes: null },
    params: {
      filter,
      ...(locationName ? { locationName } : {}),
      ...(deliveryDate ? { deliveryDate } : {}),
      page,
      size,
      ...(sort ? { sort } : {}),
      ...(deliveryType ? { deliveryType } : {}),
    },
  });
  return res.data;
};

export const fetchDeliveryDetail = async (id: number): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const res = await api.get<DeliveryDetail>(`/perfect-truck/deliveries/${id}`);
  return res.data;
};

export interface DeliveryFeedbackPayload {
  feedbackCategory?: string | null;
  feedback?: string | null;
}

export const updateDeliveryFeedback = async (
  id: number,
  payload: DeliveryFeedbackPayload,
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const { clientTimestamp, clientTimezone } = getClientTimeParams();
  const body = {
    feedbackCategory: payload.feedbackCategory ?? '',
    feedback: payload.feedback ?? '',
  };
  const res = await api.patch<DeliveryDetail>(
    `/perfect-truck/deliveries/${id}/feedback`,
    body,
    { params: { clientTimestamp, clientTimezone } },
  );
  return res.data;
};

export const searchDeliveries = async (
  searchTerm: string,
  locationName: string,
  filter: 'all' | 'open' | 'done' = 'all',
  deliveryDate?: string,
  page: number = 0,
  size: number = 20,
  feedbackPresent?: boolean,
  feedbackCategory?: string[],
  sort?: string,
  deliveryType?: string,
): Promise<PaginatedDeliveryResponse> => {
  const api = createApiClient();
  const params: Record<string, any> = {
    searchTerm,
    filter,
    locationName,
    page,
    size,
  };

  if (deliveryDate) {
    params.deliveryDate = deliveryDate;
  }
  if (sort) {
    params.sort = sort;
  }
  if (feedbackPresent === true) {
    params.feedbackPresent = true;
  }
  if (feedbackCategory && feedbackCategory.length > 0) {
    params.feedbackCategory = feedbackCategory;
  }

  const res = await api.get<PaginatedDeliveryResponse>('/perfect-truck/deliveries/search', {
    paramsSerializer: { indexes: null },
    params: {
      searchTerm,
      filter,
      locationName,
      ...(deliveryDate ? { deliveryDate } : {}),
      page,
      size,
      ...(deliveryType ? { deliveryType } : {}),
    },
  });
  return res.data;
};

export const checkRouteUniqueness = async (
  routeNumber: string,
  loadSeq: string | undefined,
  deliveryDate: string,
  locationName?: string,
): Promise<RouteUniqueness> => {
  const api = createApiClient();
  const res = await api.get<RouteUniqueness>('/perfect-truck/deliveries/check-uniqueness', {
    params: { routeNumber, deliveryDate, ...(loadSeq ? { loadSeq } : {}), ...(locationName ? { locationName } : {}) },
  });
  return res.data;
};

export const fetchRouteNumbersByLocation = async (locationName: string): Promise<string[]> => {
  const api = createApiClient();
  const res = await api.get<{ data: string[]; totalCount: number }>(
    `/perfect-truck/locations/${locationName}/routes`,
    {
      params: { isActive: true },
    }
  );
  return res.data.data;
};

export const createCertification = async (
  payload: CreateCertificationRequest,
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const res = await api.post<DeliveryDetail>('/perfect-truck/deliveries', payload, { params: getClientTimeParams() });
  return res.data;
};

export const updateCertification = async (
  id: number,
  payload: CreateCertificationRequest,
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const res = await api.put<DeliveryDetail>(`/perfect-truck/deliveries/${id}`, payload, { params: getClientTimeParams() });
  return res.data;
};

export const submitCheckerVerification = async (
  deliveryId: number,
  payload: CheckerVerificationRequest,
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const res = await api.put<DeliveryDetail>(
    `/perfect-truck/deliveries/${deliveryId}/check`,
    payload,
    { params: getClientTimeParams() },
  );
  return res.data;
};

// ── Standalone Check Record (+New Check) ──────────────────────────────────────────

export const createCheckRecord = async (
  payload: CreateCheckRequest,
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const res = await api.post<DeliveryDetail>('/perfect-truck/deliveries', payload, { params: getClientTimeParams() });
  return res.data;
};

/**
 * Creates a standalone check record (without certification) using the dedicated endpoint.
 * Used when user explicitly selects Approve or Reject for a new check without an existing certification.
 */
export const createStandaloneCheckRecord = async (
  payload: CreateCheckRequest,
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  const res = await api.post<DeliveryDetail>(
    '/perfect-truck/deliveries/checker-verification-without-certification',
    payload,
    { params: getClientTimeParams() },
  );
  return res.data;
};

/**
 * Looks up an existing certification (not a standalone check record) for the same
 * route number + load sequence + delivery date, so a new Check Record can be merged
 * into it instead of created standalone.
 */
export const findCertificationForRoute = async (
  routeNumber: string,
  loadSeq: string | undefined,
  deliveryDate: string,
  locationName?: string,
): Promise<DeliveryListItem | null> => {
  if (!locationName) return null;
  const results = await searchDeliveries(routeNumber, locationName, 'all', deliveryDate, 0, 20);
  const normalizedRoute = routeNumber.trim().toLowerCase();
  const normalizedLoadSeq = loadSeq?.trim().toLowerCase();
  const nonCheckStatuses: DeliveryStatus[] = ['Approved - No Certification', 'Rejected - No Certification'];
  return (
    results.data.find(item => {
      if (item.routeNumber.trim().toLowerCase() !== normalizedRoute) return false;
      if (item.deliveryDate !== deliveryDate) return false;
      if (normalizedLoadSeq && item.loadSeq && item.loadSeq.trim().toLowerCase() !== normalizedLoadSeq) return false;
      return !nonCheckStatuses.includes(item.status);
    }) ?? null
  );
};

export const saveDraft = async (
  payload: Partial<CreateCertificationRequest> & { id?: number; status?: DeliveryStatus },
): Promise<DeliveryDetail> => {
  const api = createApiClient();
  // If id exists, update existing draft; otherwise create new draft
  if (payload.id) {
    const { id, ...data } = payload;
    const res = await api.put<DeliveryDetail>(`/perfect-truck/deliveries/${id}`, data, { params: getClientTimeParams() });
    return res.data;
  } else {
    const res = await api.post<DeliveryDetail>('/perfect-truck/deliveries', payload, { params: getClientTimeParams() });
    return res.data;
  }
};

// ── Config ────────────────────────────────────────────────────────────────────

export interface ActiveRouteTypesResult {
  routeTypes: RouteTypeItem[];
  loadSequences: LoadSequenceItem[];
}

export const fetchActiveRouteTypes = async (language?: string): Promise<RouteTypeItem[]> => {
  const api = createApiClient();
  const res = await api.get<RouteTypeListResponse>('/perfect-truck/config/route-types', {
    params: { isActive: true, ...(language ? { language } : {}) },
  });
  return res.data.data;
};

export const fetchActiveRouteTypesWithLoadSequences = async (language?: string): Promise<ActiveRouteTypesResult> => {
  const api = createApiClient();
  const res = await api.get<RouteTypeListResponse>('/perfect-truck/config/route-types', {
    params: { isActive: true, ...(language ? { language } : {}) },
  });
  return {
    routeTypes: res.data.data,
    loadSequences: res.data.loadSequences ?? [],
  };
};

export const fetchActiveLoadSequences = async (language?: string): Promise<LoadSequenceItem[]> => {
  const api = createApiClient();
  const res = await api.get<LoadSequenceListResponse>('/perfect-truck/config/load-sequences/active', {
    params: language ? { language } : undefined,
  });
  return res.data.data;
};

export const fetchEquipmentConfigItems = async (params?: { isActive?: boolean; language?: string }): Promise<EquipmentConfigItem[]> => {
  const api = createApiClient();
  const res = await api.get<EquipmentListResponse>('/perfect-truck/config/equipment', { params });
  // Normalize historical backend flag names so frontend consistently uses `isTextBoxRequired`
  return (res.data.data || []).map((raw: any) => ({
    ...raw,
    isTextBoxRequired: !!(raw.isTextBoxRequired ?? raw.isTextFieldRequired ?? raw.textFieldRequired ?? raw.text_field_required),
  } as EquipmentConfigItem));
};

export const fetchPhotoCategories = async (language?: string): Promise<PhotoCategoryItem[]> => {
  const api = createApiClient();
  const res = await api.get<PhotoCategoryListResponse>('/perfect-truck/config/photo-categories', {
    params: language ? { language } : undefined,
  });
  return res.data.data;
};

export const fetchLocationRoutes = async (locationId: string): Promise<LocationRoute[]> => {
  const api = createApiClient();
  const res = await api.get<LocationRouteListResponse>(
    `/perfect-truck/locations/${locationId}/routes`,
  );
  return res.data.data.filter(r => r.isActive);
};

// ── Image Upload ──────────────────────────────────────────────────────────────
// containerName for Perfect Truck photos must be "nho"

export const uploadImage = async (
  fileUri: string,
  fileName: string,
  mimeType: string,
  containerName = 'nho',
): Promise<string> => {
  const api = createApiClient();
  const form = new FormData();
  form.append('image', { uri: fileUri, name: fileName, type: mimeType } as any);
  form.append('containerName', containerName);
  const res = await api.post<ImageUploadResponse>('/admin/images/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data.imageUrl;
};

// ── Perfect Truck Photo Upload ────────────────────────────────────────────────

export interface PTImageUploadResult {
  imageUrl: string;
  fileName: string;
  originalFileName: string;
  containerName: string;
}

export const uploadPerfectTruckImage = async (
  file: File | { uri: string; name: string; type: string },
  onProgress?: (progress: number) => void,
): Promise<PTImageUploadResult> => {
  const api = createApiClient();
  const form = new FormData();
  form.append('containerName', 'perfect-truck');

  if (file instanceof File) {
    // Web: File is a Blob subtype — append with explicit filename so the
    // multipart Content-Disposition includes the filename parameter.
    form.append('image', file, file.name);
  } else {
    // Native (React Native): pass the RN asset descriptor object.
    // RN's XMLHttpRequest polyfill reads the uri and constructs the
    // binary part automatically.
    form.append('image', {
      uri: file.uri,
      name: file.name,
      type: file.type,
    } as any);
  }

  // Do NOT set Content-Type manually — the HTTP client must set it with the
  // correct multipart boundary, which it does automatically when given FormData.
  const res = await api.post<ImageUploadResponse>('/perfect-truck/images/upload', form, {
    onUploadProgress: (e: any) => {
      if (onProgress && e.total) {
        onProgress(Math.min(0.95, e.loaded / e.total));
      }
    },
  });
  
  return res.data.data;
};
